import {Option, some, none} from '@danwang/option';
import Money from 'common/Money';
import * as React from 'react';
import {useUnitsById, useUnitsSettled} from 'src/hooks/useUnits';
import {TextInput} from 'src/inputs/TextInput';
import {
  formatMoneyWithUnits,
  fractionDigitsWithUnits,
  isUnitToken,
} from 'src/lib/unitDisplay';

type TextAlign = 'right' | 'left';
type Props = {
  autoFocus?: boolean;
  value: Option<Money>;
  onChange: (amount: Option<Money>) => void;
  textAlign?: TextAlign;
  disabled?: boolean;
  /** Currency the input produces (default USD). Drives the prefix glyph. */
  currency?: string;
};

/**
 * Symbol shown before the input ("$", "¥", …). Non-ISO commodity currencies
 * (TENNIS_CLASS) have no Intl symbol; fall back to no prefix — the field's
 * context names the unit.
 */
const currencyPrefix = (currency: string): string => {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? '';
  } catch {
    return '';
  }
};

const AmountInput = (props: Props) => {
  const {value, onChange} = props;
  const unitsById = useUnitsById();
  const unitsSettled = useUnitsSettled();
  // Prop wins; otherwise follow the value being edited (call sites that
  // edit an existing foreign amount get the right unit without wiring).
  // Clearing the field makes `value` none — hold the last known currency
  // rather than snapping back to USD mid-edit.
  const heldCurrency = React.useRef<string | null>(null);
  const currency =
    props.currency ??
    value.map((money) => money.currency).getOrElse(heldCurrency.current ?? 'USD');
  heldCurrency.current = currency;
  // Typed digits are read in the currency's display unit, then scaled to
  // stored hundredths — "3000" in a JPY field is ¥3,000 (300,000
  // hundredths), not ¥30. Custom units carry their own display decimals.
  const digitScale = 10 ** (2 - fractionDigitsWithUnits(currency, unitsById));
  const textAlign = Option(props.textAlign).getOrElse('left');
  const initialMoney = value.getOrElse(Money.zero(currency));
  const [draft, setDraft] = React.useState(
    formatMoneyWithUnits(initialMoney, unitsById, {showCurrency: false}),
  );
  const setMoney = (money: Money) => {
    setDraft(formatMoneyWithUnits(money, unitsById, {showCurrency: false}));
  };
  React.useEffect(() => {
    value.forEach((money) => setMoney(money));
  }, [value]);

  const parseMoney = (newValue: string): Option<Money> => {
    const hasNumbers = !!newValue.match(/\d/);
    if (hasNumbers) {
      const sign = newValue[0] === '-' ? -1 : 1;
      const numbersOnly = newValue.replace(/\D/g, '');
      const digits = numbersOnly ? parseInt(numbersOnly, 10) : 0;
      const intAmount = sign * digits * digitScale;
      return some(Money.fromInt(intAmount, currency));
    } else {
      return none;
    }
  };

  // When the currency changes under a mounted input (the create-raw field
  // autofocuses as USD before an account is picked), keep the typed DIGITS
  // and reinterpret them in the new currency's unit: "20000" entered before
  // selecting a JPY wallet means ¥20,000 — not the ¥200 that carrying the
  // old cents value across would produce. Reformats the draft too (2 ↔ 0
  // fraction digits).
  const lastCurrency = React.useRef(currency);
  React.useEffect(() => {
    if (lastCurrency.current === currency) return;
    lastCurrency.current = currency;
    // Only when an amount actually exists. With `value` empty the draft is
    // just the placeholder "0.00", and parsing it would report a real zero
    // amount to the parent — enough to enable Create and persist a
    // zero-valued raw the user never entered. That fires on its own whenever
    // an account query resolves a foreign currency after this input mounts
    // (the create-raw modal with a defaultAccountId).
    if (value.isEmpty) return;
    const parsed = parseMoney(draft);
    onChange(parsed);
    parsed.forEach((money) => setMoney(money));
    // parseMoney/setMoney are recreated per render; the ref guard makes the
    // re-runs no-ops, so listing them (per exhaustive-deps) is harmless.
  }, [currency, draft, value, onChange, parseMoney, setMoney]);

  // When the digit scale changes under the SAME currency — the units query
  // resolving a custom unit's displayDecimals after mount — the draft was
  // formatted at the old scale, and typed digits would now mis-parse ("3.00"
  // at a 0-decimal scale reads as 300). The stored value is authoritative
  // here (unlike a currency change, which preserves typed digits): reformat
  // the draft from it.
  const lastScale = React.useRef(digitScale);
  React.useEffect(() => {
    if (lastScale.current === digitScale) return;
    lastScale.current = digitScale;
    value.forEach((money) => {
      // A currency change also changes the scale, but there `value` still
      // holds the pre-switch money — the digit-preserving effect above owns
      // that case. Only reformat when the value is in the current currency.
      if (money.currency === currency) {
        setMoney(money);
      }
    });
    // setMoney is recreated per render; the ref guard makes re-runs no-ops.
  }, [digitScale, currency, value, setMoney]);

  const handleChange = (newValue: string) => {
    const parsed = parseMoney(newValue);
    onChange(parsed);
    if (parsed.isEmpty) {
      setDraft(newValue);
    } else {
      setMoney(parsed.get());
    }
  };
  const handleBlur = () => {
    const parsed = parseMoney(draft);
    setMoney(parsed.getOrElse(Money.zero(currency)));
  };

  // A unit currency whose metadata hasn't arrived yet parses at the
  // provisional 2-decimal fallback — typing "3" there stores 0.03 units and
  // the later reformat can't recover the intent. Hold entry until the units
  // query settles (a session-cached fetch, so this window is a few ms at
  // worst); a token still unknown after settling is dangling and keeps the
  // fallback behavior.
  const awaitingUnitMetadata =
    !unitsSettled && isUnitToken(currency) && !unitsById.has(currency);

  const prefix = currencyPrefix(currency);
  return (
    <div className="relative w-full">
      {prefix !== '' && (
        <div className="pointer-events-none absolute top-1/2 flex size-8 -translate-y-1/2 items-center justify-center text-sm">
          {prefix}
        </div>
      )}
      <TextInput
        autoFocus={props.autoFocus}
        type="text"
        value={draft}
        onChange={handleChange}
        onBlur={handleBlur}
        selectOnFocus
        disabled={props.disabled || awaitingUnitMetadata}
        style={{textAlign}}
        className={prefix !== '' ? 'pl-8' : undefined}
        inputMode="decimal"
      />
    </div>
  );
};

export default AmountInput;
